import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { useWorkbookPayment } from "../../hooks/useWorkbookPayment";
import { Ornament, StarGlyph, CuneiformMotif, BeeGlyph, HexDivider } from "../Ornaments";
import { BookOpen } from "lucide-react";
import { PurchasePanel } from "./workbook/PurchasePanel";

const TOC = [
  { label: "Introduction", title: "You Are Not Guessing Anymore" },
  { label: "Chapter 1", title: "The Greater Human Story" },
  { label: "Chapter 2", title: "The Inner & Outer Grid" },
  { label: "Chapter 3", title: "Coherence" },
  { label: "Chapter 4", title: "Your Somatic Signature" },
  { label: "Chapter 5", title: "Intentional Movement as Data" },
  { label: "Chapter 6", title: "Conscious Cultivation" },
  { label: "Close", title: "The Next Step" },
];

const CoverPanel = () => (
  <div
    style={{
      position: "relative",
      background:
        "linear-gradient(160deg, rgba(42,74,122,0.5) 0%, rgba(74,42,90,0.35) 50%, rgba(10,8,6,1) 100%)",
      padding: "2.5rem 2rem",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: 480,
      borderRight: "1px solid rgba(201, 168, 76, 0.15)",
      overflow: "hidden",
    }}
  >
    {/* Real macro honeycomb photo across the cover panel */}
    <div
      className="honeycomb-panel"
      style={{ opacity: 0.55, backgroundImage: "url('/media/honeycomb-real.jpg')", backgroundSize: "500px" }}
      aria-hidden="true"
    />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative" }}>
      <span className="font-heading" style={{ color: "var(--gold)", fontSize: "0.6rem", letterSpacing: "0.4em" }}>
        Somatic Equilibrium
      </span>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem" }}>
        <BeeGlyph size={16} />
        <StarGlyph size={14} />
      </div>
    </div>
    <div style={{ position: "relative" }}>
      <p
        className="font-display"
        style={{
          color: "var(--gold-bright)",
          fontSize: "clamp(1.5rem, 2.6vw, 2rem)",
          fontWeight: 700,
          lineHeight: 1.15,
          margin: "0 0 0.5rem",
        }}
      >
        The Introductory Workbook
      </p>
      <p
        className="italic-quote"
        style={{ color: "var(--text)", fontSize: "0.95rem", lineHeight: 1.5, margin: 0 }}
      >
        Know your grid. Cultivate your signature. Become who you are.
      </p>
    </div>
    <div style={{ position: "relative" }}>
      <CuneiformMotif width={180} />
      <div style={{ marginTop: "0.6rem" }}>
        <HexDivider width={180} />
      </div>
      <p className="font-heading" style={{ color: "var(--text-dim)", fontSize: "0.6rem", letterSpacing: "0.35em", marginTop: "1rem" }}>
        Phillip Obi Schlachter &nbsp;·&nbsp; Benjamin D&rsquo;Aguilar
      </p>
    </div>
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        right: -1,
        top: 0,
        bottom: 0,
        width: 8,
        background: "linear-gradient(180deg, transparent, var(--gold), transparent)",
        opacity: 0.4,
      }}
    />
  </div>
);

const TocList = () => (
  <ul style={{ listStyle: "none", margin: 0, padding: 0 }} data-testid="workbook-toc">
    {TOC.map((c, i) => (
      <li
        key={c.label}
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          gap: "0.85rem",
          alignItems: "baseline",
          padding: "0.55rem 0",
          borderBottom: i < TOC.length - 1 ? "1px solid rgba(201,168,76,0.08)" : "none",
        }}
      >
        <span className="font-heading" style={{ color: "var(--gold)", fontSize: "0.6rem", letterSpacing: "0.32em", whiteSpace: "nowrap" }}>
          {c.label}
        </span>
        <span style={{ color: "var(--text)", fontSize: "0.92rem", fontFamily: "var(--font-body)" }}>
          {c.title}
        </span>
        <span style={{ color: "var(--text-dim)", fontSize: "0.65rem" }}>·</span>
      </li>
    ))}
  </ul>
);

export const Workbook = () => {
  const head = useReveal();
  const card = useReveal();
  const payment = useWorkbookPayment();

  return (
    <section
      id="workbook"
      data-testid="workbook-section"
      style={{
        position: "relative",
        padding: "7rem 1.5rem",
        background: "linear-gradient(180deg, #0a0806 0%, #14100c 50%, #0a0806 100%)",
        overflow: "hidden",
      }}
    >
      <div className="gold-glow" style={{ width: 700, height: 700, top: -200, right: -150, opacity: 0.55 }} />
      <div className="lapis-glow" style={{ width: 600, height: 600, bottom: -150, left: -150, opacity: 0.6 }} />

      <div className="section-inner" style={{ maxWidth: 1100 }}>
        <div ref={head.ref} className={`reveal ${head.inView ? "in-view" : ""}`} style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 3.5rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            The Workbook
          </p>
          <h2 className="section-title" data-testid="workbook-title">
            The Introductory Workbook
          </h2>
          <p className="italic-quote" style={{ color: "var(--text-muted)", maxWidth: 600, margin: "0 auto", lineHeight: 1.8 }}>
            Know your grid. Cultivate your signature. Become who you are.
          </p>
        </div>

        <div
          ref={card.ref}
          className={`reveal ${card.inView ? "in-view" : ""}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "0",
            border: "1px solid rgba(201, 168, 76, 0.3)",
            background: "linear-gradient(180deg, rgba(20,16,12,0.8) 0%, rgba(10,8,6,0.95) 100%)",
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.85)",
          }}
          data-testid="workbook-card"
        >
          <div className="workbook-grid">
            <CoverPanel />

            {/* Right — TOC + Purchase */}
            <div style={{ padding: "2.5rem 2rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
                <BookOpen size={14} color="var(--gold)" />
                <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.68rem", letterSpacing: "0.32em", margin: 0 }}>
                  What&rsquo;s Inside
                </p>
              </div>

              <TocList />

              <div style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px dashed rgba(201,168,76,0.25)" }}>
                <PurchasePanel {...payment} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
